import apiClient from './client'
import { User } from '../../contexts/AuthContext'

export interface LoginRequest {
  email: string
  password: string
  remember_me?: boolean
}

export interface GoogleAuthRequest {
  provider: 'google'
  access_token?: string
  id_token?: string
  code?: string
}

export interface AuthTokens {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface PasswordChangeRequest {
  current_password: string
  new_password: string
  confirm_password: string
}

export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetConfirm {
  token: string
  new_password: string
  confirm_password: string
}

export const authApi = {
  // Authentication
  login: async (data: LoginRequest): Promise<AuthTokens> => {
    const response = await apiClient.post('/auth/login', data)
    return response.data
  },

  googleAuth: async (data: GoogleAuthRequest): Promise<AuthTokens> => {
    const response = await apiClient.post('/oauth/google', data)
    return response.data
  },

  logout: async (refreshToken: string): Promise<void> => {
    await apiClient.post('/auth/logout', { refresh_token: refreshToken })
  },

  logoutAll: async (): Promise<void> => {
    await apiClient.post('/auth/logout-all')
  },

  refreshToken: async (refreshToken: string): Promise<AuthTokens> => {
    const response = await apiClient.post('/auth/refresh', { 
      refresh_token: refreshToken 
    })
    return response.data
  },

  // User profile
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me')
    return response.data
  },

  // Password management
  changePassword: async (data: PasswordChangeRequest): Promise<void> => {
    await apiClient.post('/auth/change-password', data)
  },

  requestPasswordReset: async (data: PasswordResetRequest): Promise<void> => {
    await apiClient.post('/auth/forgot-password', data)
  },

  resetPassword: async (data: PasswordResetConfirm): Promise<void> => {
    await apiClient.post('/auth/reset-password', data)
  },

  // Email verification
  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post('/auth/verify-email', { token })
  },

  resendVerification: async (email: string): Promise<void> => {
    await apiClient.post('/auth/resend-verification', { email })
  },

  // Account approval workflow
  reRequestAccess: async (): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.post('/auth/re-request')
    return response.data
  },

  // OAuth management
  disconnectSocialAccount: async (provider: string): Promise<void> => {
    await apiClient.post(`/oauth/disconnect/${provider}`)
  },

  getGoogleAuthUrl: async (redirectUri: string, state?: string): Promise<{ auth_url: string }> => {
    const params = new URLSearchParams({ redirect_uri: redirectUri })
    if (state) params.append('state', state)
    
    const response = await apiClient.get(`/oauth/google/url?${params}`)
    return response.data
  },
}
